import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaAd,
  FaBullhorn,
  FaWallet,
  FaChartBar,
  FaSync,
  FaEye,
  FaGift,
  FaRocket,
  FaCreditCard
} from "react-icons/fa";

const InfluencerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const dashboardCards = [
    {
      title: "Paid Campaigns",
      icon: <FaAd />,
      path: "/influencer/advertisement",
      color: "from-purple-600 to-indigo-700",
      shadow: "shadow-purple-100",
      description: "Premium Leads"
    },
    {
      title: "Reward Tasks",
      icon: <FaGift />,
      path: "/influencer/free-ads",
      color: "from-emerald-500 to-teal-700",
      shadow: "shadow-emerald-100",
      description: "Mandatory Ads"
    },
    {
      title: "Public Voice",
      icon: <FaBullhorn />,
      path: "/influencer/raise-your-voice",
      color: "from-amber-500 to-orange-700",
      shadow: "shadow-amber-100",
      description: "Share Concerns"
    },
    {
      title: "My Earnings",
      icon: <FaWallet />,
      path: "/influencer/wallet",
      color: "from-slate-700 to-slate-900",
      shadow: "shadow-slate-100",
      description: "Wallet Details"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Loading Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-10">
      {/* Sleek Premium Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-6 sm:px-8 sm:py-8 mb-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Live Influencer Status</span>
            </div>
            <h1 className="text-2xl font-[1000] text-slate-900 tracking-tight">Content <span className="text-purple-600">Hub.</span></h1>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <div className="bg-slate-50 p-2 rounded-2xl border border-slate-100">
              <FaRocket className="text-purple-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
          {dashboardCards.map((card, index) => (
            <button
              key={index}
              onClick={() => navigate(card.path)}
              className={`
                relative overflow-hidden group p-4 sm:p-6 rounded-[2rem] bg-gradient-to-br ${card.color}
                ${card.shadow} shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-95 text-left
              `}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 blur-2xl group-hover:scale-150 transition-transform duration-700" />

              <div className="flex justify-between items-start mb-6">
                <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl border border-white/20 shadow-lg text-white text-xl sm:text-2xl">
                  {card.icon}
                </div>
                <div className="bg-white/10 px-2 py-1 rounded-full border border-white/5">
                  <FaEye className="text-white/40 text-[10px]" />
                </div>
              </div>

              <div className="space-y-1">
                <h2 className="text-white font-black text-sm sm:text-base tracking-tight">{card.title}</h2>
                <p className="text-white/60 text-[9px] font-bold uppercase tracking-widest">{card.description}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Action Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/influencer/id-card')}
            className="p-5 rounded-[2rem] bg-white border border-slate-100 shadow-xl shadow-slate-200/50 flex items-center justify-between group hover:border-purple-300 transition-all active:scale-95"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white text-xl">
                <FaCreditCard />
              </div>
              <div className="text-left">
                <h2 className="text-slate-900 font-black text-sm sm:text-base tracking-tight">Identity Management</h2>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Verify Credentials</p>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-purple-600 group-hover:text-white transition-all">
              <FaSync className="text-xs group-hover:rotate-180 transition-transform duration-500" />
            </div>
          </button>

          <button
            onClick={() => window.location.reload()}
            className="p-5 rounded-[2rem] bg-white border border-slate-100 shadow-xl shadow-slate-200/50 flex items-center justify-between group hover:border-pink-300 transition-all active:scale-95"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 text-xl">
                <FaSync />
              </div>
              <div className="text-left">
                <h2 className="text-slate-900 font-black text-sm sm:text-base tracking-tight">Refresh Assets</h2>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Update Analytics</p>
              </div>
            </div>
            <div className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-black uppercase tracking-widest transition-all group-hover:bg-emerald-500 group-hover:text-white">Active</div>
          </button>
        </div>

        {/* Summary Banner */}
        <div className="mt-6 p-4 rounded-3xl bg-indigo-50 border border-indigo-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
              <FaChartBar className="text-xs" />
            </div>
            <p className="text-indigo-900 text-[11px] font-[900] uppercase tracking-wider">Performance Intelligence is active</p>
          </div>
          <span className="text-[10px] font-black text-indigo-400 uppercase">Live Insights</span>
        </div>
      </div>
    </div>
  );
};

export default InfluencerDashboard;
